import { applySystemEdit, systemEditRejection } from "../ai/system-edit";
import { buildSystemDigest } from "../ai/system-digest";
import { fuseGeneratedSystem } from "../ai/system-fuse";
import { planComponentReplace } from "../component-replace";
import { getDefinition } from "../catalog";
import { planComponentCustomization } from "../component-customize";
import { refreshAutoFuses, computeSystemCableSpec } from "../cable-spec";
import { fuseExceedsTerminalLimit } from "../terminal-fuse-limit";
import { runAllDiagnostics } from "../diagnostics";
import { cableEndpointStatus } from "../diagnostics/cable-endpoints";
import { assistantIssueId, assistantSystemFingerprint } from "./context";
import { specValueAllowed, verifiedSpecSchema } from "./spec-verification";
import { assistantSystemIsValid } from "./validation";
import type { AssistantProposal, AssistantProposalResult } from "./types";
import type { Cable, Issue, IssueFixAction, System } from "../types";

const INCOMPLETE_CODES = new Set(["load-no-positive", "load-no-return", "load-floating", "load-via-floating-converter", "charger-return-floating", "source-return-floating", "current-model-unverified", "parallel-battery-chemistry-mismatch", "duplicate-cable", "pv-input-incomplete", "monitor-power-unverified", "missing-ground", "battery-isolator-model-unverified", "load-current-unverified"]);
const diagnosticKey = (issue: Issue) => `${assistantIssueId(issue)}:${JSON.stringify(issue.message)}`;

/** This diagnostic aggregates affected cables separately from the components
 * and reasons that the solver cannot model. Completing a missing return can
 * append that new cable without introducing another uncertainty. Keep the
 * warning, but do not make its changing provenance prevent the repair.
 * Changed causes/specifications or newly affected existing circuits still fail. */
function preservesCurrentUncertainty(issue: Issue, before: Issue[], system: System, candidate: System): boolean {
  if (issue.code !== "current-model-unverified" || issue.severity !== "warning" || !issue.nodeIds?.length) return false;
  if (issue.nodeIds.some(id => {
    const original = system.nodes.find(node => node.id === id);
    return !original || JSON.stringify(original) !== JSON.stringify(candidate.nodes.find(node => node.id === id));
  })) return false;
  const meaning = (item: Issue) => {
    const semantic = { ...item, nodeIds: [...(item.nodeIds ?? [])].sort() };
    delete semantic.cableIds;
    return JSON.stringify(semantic);
  };
  const existingCables = new Set(system.cables.map(cable => cable.id));
  return before.some(previous => {
    if (meaning(previous) !== meaning(issue)) return false;
    const affectedCables = new Set(previous.cableIds);
    const affectedNodes = new Set(system.cables.filter(cable => affectedCables.has(cable.id))
      .flatMap(cable => [cable.fromNodeId, cable.toNodeId]));
    return (issue.cableIds ?? []).every(id => {
      if (existingCables.has(id)) return affectedCables.has(id);
      const added = candidate.cables.find(cable => cable.id === id);
      return added && affectedNodes.has(added.fromNodeId) && affectedNodes.has(added.toNodeId);
    });
  });
}

/** Removing an extra B+ branch fixes use of the sense post as a bus. Its
 * remaining supply may still have the same unverified fuse declaration. The
 * monitor diagnostic lists every incident cable, so this repair shrinks that
 * list without introducing a new protection problem. Allow only that removal:
 * the monitor and every retained cable must be unchanged, with one original
 * B+ lead left. A new/changed supply, return, or diagnostic reason still fails. */
function preservesMonitorProtection(issue: Issue, before: Issue[], system: System, candidate: System): boolean {
  if (issue.code !== "monitor-power-unverified" || issue.severity !== "warning" ||
      issue.message.key !== "diag.monitor-power-unverified.message_protection" || issue.nodeIds?.length !== 1) return false;
  const nodeId = issue.nodeIds[0];
  const original = system.nodes.find(node => node.id === nodeId);
  if (!original || JSON.stringify(original) !== JSON.stringify(candidate.nodes.find(node => node.id === nodeId))) return false;
  const meaning = (item: Issue) => {
    const semantic = { ...item };
    delete semantic.cableIds;
    return JSON.stringify(semantic);
  };
  const previous = before.find(item => meaning(item) === meaning(issue));
  const remainingIds = new Set(issue.cableIds);
  if (!previous?.cableIds || !remainingIds.size || remainingIds.size >= previous.cableIds.length) return false;
  if ([...remainingIds].some(id => !previous.cableIds!.includes(id) ||
      JSON.stringify(system.cables.find(cable => cable.id === id)) !== JSON.stringify(candidate.cables.find(cable => cable.id === id)))) return false;
  const isSupplyLead = (cable: Cable) => (cable.fromNodeId === nodeId && cable.fromTerminalId === "vbat") ||
    (cable.toNodeId === nodeId && cable.toTerminalId === "vbat");
  if (candidate.cables.filter(isSupplyLead).length !== 1) return false;
  const removedIds = previous.cableIds.filter(id => !remainingIds.has(id));
  const senseIssue = before.find(item => item.code === "sense-terminal-as-bus" && item.nodeIds?.includes(nodeId) &&
    removedIds.every(id => item.cableIds?.includes(id)));
  return !!senseIssue && removedIds.every(id => {
    const cable = system.cables.find(item => item.id === id);
    return cable && isSupplyLead(cable) && !candidate.cables.some(item => item.id === id);
  });
}

/** A repair may leave an unrelated disconnected cable in place, but may not
 * create a disconnected end or move a cable onto an invalid terminal. */
function preservesDisconnectedCables(system: System, candidate: System): boolean {
  const before = cableEndpointStatus(system, getDefinition);
  const after = cableEndpointStatus(candidate, getDefinition);
  const originals = new Map(system.cables.map(cable => [cable.id, cable]));
  return candidate.cables.every(cable => {
    const original = originals.get(cable.id);
    return (["from", "to"] as const).every(end => {
      const node = cable[`${end}NodeId`], terminal = cable[`${end}TerminalId`];
      const status = after(node, terminal);
      return status === "valid" || (original?.fromNodeId === cable.fromNodeId &&
        original.fromTerminalId === cable.fromTerminalId && original.toNodeId === cable.toNodeId &&
        original.toTerminalId === cable.toTerminalId && status === before(node, terminal));
    });
  });
}

/** Deterministic preview and final apply use exactly this same validator. */
export function prepareAssistantProposal(
  system: System,
  proposal: AssistantProposal,
  options: { autoSizeTargetPct?: number } = {},
): AssistantProposalResult {
  if (assistantSystemFingerprint(system) !== proposal.baseFingerprint) return { ok: false, error: "The drawing changed after this proposal was prepared. Ask for an updated proposal before applying." };
  if ((proposal.autoSizeTargetPct ?? 3) !== (options.autoSizeTargetPct ?? 3)) return { ok: false, error: "The sizing preference changed after this proposal. Ask for an updated proposal before applying." };
  const before = runAllDiagnostics(system, getDefinition, undefined, undefined, options.autoSizeTargetPct).issues;
  let candidate = system;
  const originalRefs = buildSystemDigest(system).refToNodeId;
  if (proposal.specUpdates?.length) {
    if (proposal.specUpdates.length > 24 || proposal.topology || proposal.replacements?.length || proposal.repairIssueIds.length) return { ok: false, error: "Specification verification cannot change equipment or wiring." };
    const seen = new Set<string>();
    for (const raw of proposal.specUpdates) {
      const parsed = verifiedSpecSchema.safeParse(raw);
      if (!parsed.success) return { ok: false, error: "The specification source is invalid." };
      const update = parsed.data;
      const key = `${update.nodeId}:${update.field}`;
      const node = candidate.nodes.find(n => n.id === update.nodeId);
      if (seen.has(key) || !node || node.kind !== "component" || node.definitionId !== "custom" || node.customDefinition?.source !== "ai" || !specValueAllowed(node.customDefinition, update.field, update.value)) return { ok: false, error: "This field is no longer an AI estimate. Request a fresh review." };
      seen.add(key);
      // Use the same derived terminal/current-limit updates as a manual edit,
      // so a stale estimated terminal cap cannot clamp the corrected demand.
      const plan = planComponentCustomization(candidate, node.id, { [update.field]: update.value });
      candidate = { ...candidate, nodes: plan.nodes.map(n => n.id === node.id && n.kind === "component" ? { ...n, customDefinition: {
        ...n.customDefinition!, specSources: { ...n.customDefinition?.specSources, [update.field]: { url: update.sourceUrl, title: update.sourceTitle, quote: update.quote } },
      } } : n) };
      if (plan.electricalChanged) candidate = { ...candidate, cables: refreshAutoFuses(candidate, undefined, options.autoSizeTargetPct) };
    }
  }
  for (const replacement of proposal.replacements ?? []) {
    const nodeId = originalRefs.get(replacement.ref);
    const plan = nodeId ? planComponentReplace(candidate, nodeId, replacement.catalogId) : null;
    if (!plan) return { ok: false, error: "The requested replacement cannot preserve the existing terminal connections. Check the exact replacement model." };
    candidate = { ...candidate, nodes: candidate.nodes.map(n => n.id === nodeId ? plan.node : n), cables: plan.updatedCables };
  }
  if (proposal.topology) {
    const applied = applySystemEdit(candidate, originalRefs, proposal.topology);
    if (!applied.ok) return { ok: false, error: systemEditRejection(applied.error) };
    // Existing cables keep their declared protection; only new cables are sized.
    candidate = fuseGeneratedSystem(applied.system, new Set(applied.addedCableIds), options.autoSizeTargetPct);
  }
  for (const id of proposal.repairIssueIds) {
    const issue = runAllDiagnostics(candidate, getDefinition, undefined, undefined, options.autoSizeTargetPct).issues.find(i => assistantIssueId(i) === id);
    if (!issue?.fixAction) return { ok: false, error: "This warning no longer has the proposed automatic repair. Refresh the proposal." };
    candidate = applyEngineRepair(candidate, issue.fixAction, options.autoSizeTargetPct);
  }
  if (candidate === system || JSON.stringify(candidate) === JSON.stringify(system)) return { ok: false, error: "This proposal does not change the drawing." };
  if (!assistantSystemIsValid(candidate) || !preservesDisconnectedCables(system, candidate)) return { ok: false, error: "The proposed connections do not match the equipment terminals." };
  const after = runAllDiagnostics(candidate, getDefinition, undefined, undefined, options.autoSizeTargetPct).issues;
  const preexisting = new Set(before.map(diagnosticKey));
  const newBlocking = after.filter(i => (i.severity === "critical" || INCOMPLETE_CODES.has(i.code)) &&
    !(i.code === "current-model-unverified"
      ? preservesCurrentUncertainty(i, before, system, candidate)
      : preexisting.has(diagnosticKey(i)) || preservesMonitorProtection(i, before, system, candidate)));
  // Correcting a documented input can reveal a fault already present in the
  // drawing. Keep that truthful input and the resulting diagnostic. This
  // exception is limited above to spec-only edits; wiring repairs still fail.
  if (newBlocking.length && !proposal.specUpdates?.length) return { ok: false, error: `The change introduces an electrical issue: ${[...new Set(newBlocking.map(i => i.code))].join(", ")}. Revise the proposal so it has complete, supported circuits and no new critical faults.` };
  return {
    ok: true, system: candidate,
    ...diff(system.nodes, candidate.nodes, "Node"), ...diff(system.cables, candidate.cables, "Cable"),
    issuesBefore: before, issuesAfter: after,
  };
}

function diff<T extends { id: string }, K extends "Node" | "Cable">(before: T[], after: T[], kind: K) {
  const old = new Map(before.map(item => [item.id, item]));
  const next = new Map(after.map(item => [item.id, item]));
  return {
    [`added${kind}Ids`]: after.filter(item => !old.has(item.id)).map(item => item.id),
    [`removed${kind}Ids`]: before.filter(item => !next.has(item.id)).map(item => item.id),
    [`changed${kind}Ids`]: after.filter(item => old.has(item.id) && JSON.stringify(item) !== JSON.stringify(old.get(item.id))).map(item => item.id),
  } as Record<`added${K}Ids` | `removed${K}Ids` | `changed${K}Ids`, string[]>;
}

function applyEngineRepair(system: System, action: IssueFixAction, target?: number): System {
  if (action.kind === "delete-cables") {
    const removed = new Set(action.cableIds);
    return { ...system, cables: system.cables.filter(c => !removed.has(c.id)) };
  }
  let cables = system.cables.map((c): Cable => {
    if (c.id !== action.cableId) return c;
    switch (action.kind) {
      case "set-cable-wire": return { ...c, wireSizeMm2Override: action.mm2 };
      case "set-cable-fuse": return { ...c, fuseAmpOverride: action.rating, fuseAmpAuto: false };
      case "set-cable-series-link": return { ...c, seriesLink: action.seriesLink || undefined };
      case "re-point-cable": return { ...c, fromNodeId: action.fromNodeId, fromTerminalId: action.fromTerminalId, toNodeId: action.toNodeId, toTerminalId: action.toTerminalId };
    }
  });
  if (action.kind === "re-point-cable") {
    const tentative = { ...system, cables };
    cables = cables.map(c => {
      if (c.id !== action.cableId || c.fuseAmpOverride == null) return c;
      const bare = { ...tentative, cables: tentative.cables.map(item => item.id === c.id ? { ...item, fuseAmpOverride: null } : item) };
      const spec = computeSystemCableSpec(bare.cables.find(item => item.id === c.id)!, bare, getDefinition, undefined, target);
      if (spec.sizingVerificationRequired || spec.manufacturerInstallationUnverified || spec.fuseRecommendationUnavailableReason ||
          fuseExceedsTerminalLimit(spec, spec.fuse) || fuseExceedsTerminalLimit(spec, c.fuseAmpOverride)) return c;
      return spec.isAc ? { ...c, fuseAmpOverride: null } : spec.fuse > 0 ? { ...c, fuseAmpOverride: spec.fuse } : c;
    });
  }
  return { ...system, cables };
}
